import {
  useGetCostReportQuery,
  useGetRevenueReportQuery
} from 'app/apis/advanced-reports/advanced-report-query-api';
import { AttributionModelTypes } from 'common/constants/attribution-model-types';
import { toLocalIso } from 'common/utils/date-helpers';
import { AdvancedReportsQueryCategories } from 'features/advanced-reports/constants/advanced-reports-query-categories';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { SegmentTypes } from 'features/segment-controls/constants/segment-types';
import { Segment } from 'features/segment-controls/segment-controls.types';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as guid } from 'uuid';
import { createMmmCampaignDrilldownModelOutputs } from './factories/mmm-campaign-drilldown-model-output-helpers';
import {
  getCoreRowModel,
  SortingState,
  useReactTable,
  ColumnResizeMode
} from '@tanstack/react-table';
import { getMmmCampaignDrilldownOutputTableColumnDefs } from './mmm-campaign-drilldown-output-table-column-defs';
import { DrillthroughTable } from 'common/components/table/drillthrough-table/drillthrough-table';
import { selectOpenRowIds } from 'features/advanced-reports/table/advanced-reports-table.slice';
import { DrillthroughTableInjectableRow } from 'common/components/table/drillthrough-table/drillthrough-table.types';
import { createDrillthroughTableInjectableRow } from './factories/mmm-campaign-drilldown-table-injectable-row-factory';
import { useGetDataScienceMmmOutputResultsQuery } from 'app/apis/queryApi';
import {
  selectSelectedBudgetScenario,
  selectSelectedModelRunDetails,
  selectSelectedSegmentKey,
  selectSelectedTimeType
} from './campaign-mmm-controls/campaign-mmm-control.slice';
import { RootState } from 'app/store';
import {
  selectIsEditingCurrentSelectedBudgetScenario,
  selectUpdatedBudgetScenarios
} from '../budget-scenario-planner/edit-budget-scenario.slice';
import { selectMmmOutputWithInEditBudgetScenarios } from '../budget-scenario-planner/mmm-output-results.selectors';

const segments: Segment[] = [
  {
    id: guid(),
    order: 0,
    segmentType: SegmentTypes.CUSTOM_DATA_KEY,
    segmentCategory: 'campaign group' // needs to be renamed
  },
  {
    id: guid(),
    order: 0,
    segmentType: SegmentTypes.CUSTOM_DATA_KEY,
    segmentCategory: 'campaign'
  }
];

const MmmCampaignDrillDownOutputTable: FC = () => {
  const siteId = useSelector(selectSiteId);
  const dates = useSelector(getDateRangeAsObjects);
  const selectedCurrencyCode = useSelector(selectCurrencyCode);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const openRowIds = useSelector(selectOpenRowIds);
  const modelRunDetails = useSelector(selectSelectedModelRunDetails);
  const selectedBudgetScenario = useSelector(selectSelectedBudgetScenario);
  const timeRangeType = useSelector(selectSelectedTimeType);
  const segmentKey = useSelector(selectSelectedSegmentKey);

  const modelRunId = modelRunDetails?.modelRunName ?? '';
  const modelRunDate = modelRunDetails?.targetDate ?? '';

  const updatedCompareDataFromBudgetScenario = useSelector((state: RootState) =>
    selectUpdatedBudgetScenarios(state, selectedBudgetScenario)
  );

  const isCurrentlyEditingChosenBudgetScenario = useSelector(
    (state: RootState) =>
      selectIsEditingCurrentSelectedBudgetScenario(
        state,
        selectedBudgetScenario
      )
  );

  const { mmmOutput, isUninitializedMmmOutput } =
    useGetDataScienceMmmOutputResultsQuery(
      {
        siteId,
        modelRunId,
        modelRunDate,
        segment: segmentKey ?? '',
        modelType: isCurrentlyEditingChosenBudgetScenario
          ? updatedCompareDataFromBudgetScenario?.originalScenarioName ??
            selectedBudgetScenario?.scenarioName ??
            ''
          : selectedBudgetScenario?.scenarioName ?? '',
        timeRangeType: timeRangeType ?? ''
      },
      {
        selectFromResult: (result) => {
          const { isFetching, isUninitialized } = result;
          return {
            mmmOutput: selectMmmOutputWithInEditBudgetScenarios(
              result,
              isFetching,
              updatedCompareDataFromBudgetScenario
            ),
            isUninitializedMmmOutput: isUninitialized
          };
        },
        skip:
          !siteId ||
          !modelRunId ||
          !modelRunDate ||
          !segmentKey ||
          !selectedBudgetScenario ||
          !selectedBudgetScenario.scenarioName
      }
    );

  const {
    isLoading: isRevenueLoading,
    isFetching: isRevenueFetching,
    revenueRows
  } = useGetRevenueReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.REVENUE,
      attributionModelName: AttributionModelTypes.LastClick,
      currencyCode: selectedCurrencyCode,
      segmentRequests: segments.map((segment) => ({
        segmentId: segment.id,
        segmentType: segment.segmentType,
        segmentCategory: segment.segmentCategory,
        order: segment.order,
        filters: []
      }))
    },
    {
      selectFromResult: (result) => {
        const { isLoading, isFetching } = result;
        return {
          revenueRows: result.data,
          isLoading,
          isFetching
        };
      }
    }
  );

  const {
    isLoading: isCostLoading,
    isFetching: isCostFetching,
    costRows
  } = useGetCostReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.AD_COST,
      attributionModelName: AttributionModelTypes.LastClick,
      currencyCode: selectedCurrencyCode,
      segmentRequests: segments.map((segment) => ({
        segmentId: segment.id,
        segmentType: segment.segmentType,
        segmentCategory: segment.segmentCategory,
        order: segment.order,
        filters: []
      }))
    },
    {
      selectFromResult: (result) => {
        const { isLoading, isFetching } = result;
        return {
          costRows: result.data,
          isLoading,
          isFetching
        };
      }
    }
  );

  const {
    isLoading: isSalesLoading,
    isFetching: isSalesFetching,
    salesRows
  } = useGetRevenueReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.SALES,
      attributionModelName: AttributionModelTypes.LastClick,
      currencyCode: selectedCurrencyCode,
      segmentRequests: segments.map((segment) => ({
        segmentId: segment.id,
        segmentType: segment.segmentType,
        segmentCategory: segment.segmentCategory,
        order: segment.order,
        filters: []
      }))
    },
    {
      selectFromResult: (result) => {
        const { isLoading, isFetching } = result;
        return {
          salesRows: result.data,
          isLoading,
          isFetching
        };
      }
    }
  );

  const columnDefs = getMmmCampaignDrilldownOutputTableColumnDefs();

  const campaignDrilldownRows = useMemo(() => {
    const metricRows = createMmmCampaignDrilldownModelOutputs(
      {
        revenues: revenueRows ?? [],
        costs: costRows ?? [],
        impressions: [],
        clicks: [],
        calls: [],
        webs: [],
        opps: [],
        oppValues: [],
        sales: salesRows ?? []
      },
      mmmOutput ?? []
    );

    return metricRows;
  }, [revenueRows, costRows, mmmOutput, salesRows]);

  const rootRows = useMemo(
    () => campaignDrilldownRows.filter((row) => row.parentId === ''),
    [campaignDrilldownRows]
  );

  const drillthroughRows = useMemo(() => {
    const rows = campaignDrilldownRows
      .filter((row) => openRowIds.includes(row.parentId))
      .filter((row) => !(row.field === '(not set)'))
      .map<DrillthroughTableInjectableRow>((row) =>
        createDrillthroughTableInjectableRow(
          row,
          columnDefs.map((column) => column.id!)
        )
      );

    return rows;
  }, [campaignDrilldownRows, columnDefs, openRowIds]);

  const mmmCampaignDrilldownTable = useReactTable({
    data: rootRows,
    columns: columnDefs,
    manualSorting: true,
    enableColumnResizing: true,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode
  });

  return (
    <div className="mmm-campaign-drilldown-output-table">
      {(isRevenueLoading || isCostLoading) && (
        <div className="loading-indicator">Loading...</div>
      )}

      <DrillthroughTable
        key={`advanced-reports-table`}
        componentId={'advanced-reports-table'}
        heightClass="max-h-2/3-screen"
        table={mmmCampaignDrilldownTable}
        totalRow={undefined}
        injectableRows={drillthroughRows}
      />
    </div>
  );
};

export default MmmCampaignDrillDownOutputTable;
